import {useMemo, useState} from 'react';
import {EmbeddedPage, PageStatus} from "@tinct/core-api-models";
import {getEffectiveStatus} from '../../../pages/shared';

const TARGET_STATUSES = ['ready', 'error', 'generating', 'published', 'unpublished', 'excluded', 'excluded_conflict'] as const;

const TARGET_STATUS_ORDER: Record<string, number> = {
  ready: 0,
  error: 1,
  generating: 2,
  unpublished: 3,
  published: 4,
  excluded: 5,
  excluded_conflict: 6,
};

export type StatusFilterValue =
  | 'all'
  | 'ready'
  | 'generating'
  | 'published'
  | 'unpublished'
  | 'excluded'
  | 'excluded_conflict'
  | 'error';

export const STATUS_FILTER_OPTIONS: {
  value: StatusFilterValue;
  label: string;
  statuses: PageStatus[];
}[] = [
  { value: 'all', label: 'All', statuses: [] },
  { value: 'ready', label: 'Ready', statuses: ['ready'] },
  { value: 'generating', label: 'Generating', statuses: ['generating'] },
  { value: 'published', label: 'Published', statuses: ['published'] },
  { value: 'unpublished', label: 'Unpublished', statuses: ['unpublished'] },
  { value: 'excluded', label: 'Excluded', statuses: ['excluded'] },
  { value: 'excluded_conflict', label: 'Excluded conflict', statuses: ['excluded_conflict'] },
  { value: 'error', label: 'Error', statuses: ['error'] },
];

const matchesSearch = (account: EmbeddedPage, query: string) => {
  // Ad pages have no company — match their ads-campaign name instead.
  const name = account.lead?.company?.name ?? account.adCampaign?.name ?? '';
  return name.toLowerCase().includes(query);
};

const EXCLUDED_STATUSES: PageStatus[] = ['excluded', 'excluded_conflict'];

// A page belongs to the target list purely on its status: it has been
// generated, so it can be previewed, searched and filtered. Being flagged
// `needs_regeneration` doesn't change that — the page is still live and
// selectable, it just also counts as pending work (see `totalCount` below).
// Exported for the studio, which needs the same split for its variants-tab
// badge count and default page selection.
export const isTargetPage = (page: EmbeddedPage) =>
  TARGET_STATUSES.includes(page.status as typeof TARGET_STATUSES[number]);

// Stale pages that "Generate all" will actually regenerate. The backend skips
// excluded pages outright, and published ones unless they are stale
// (CampaignAudienceService#generateAllPages), so excluded pages never count
// however stale they are.
const needsRegeneration = (page: EmbeddedPage) =>
  page.outdatedStatus === 'needs_regeneration' &&
  !EXCLUDED_STATUSES.includes(page.status as PageStatus);

// Panel display order for generated pages: ready first, then by status, ties
// broken by id so the order is stable across refetches. Exported so the studio
// can default-select the page shown at the top of the panel.
export const sortTargetPages = (pages: EmbeddedPage[]): EmbeddedPage[] =>
  [...pages].sort((a, b) => {
    const statusDiff =
      (TARGET_STATUS_ORDER[a.status ?? ''] ?? 99) - (TARGET_STATUS_ORDER[b.status ?? ''] ?? 99);
    if (statusDiff !== 0) return statusDiff;
    return (a.id ?? '').localeCompare(b.id ?? '');
  });

export const usePageSearch = (pages: EmbeddedPage[], initialQuery = '') => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');

  const allTargetPages = useMemo(
    () => sortTargetPages(pages.filter(isTargetPage)),
    [pages]
  );

  const queuedPages = useMemo(
    () => pages.filter((page) => !isTargetPage(page)),
    [pages]
  );

  const activeOption = useMemo(
    () => STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_FILTER_OPTIONS[0],
    [statusFilter]
  );

  const isFiltered = statusFilter !== 'all';

  // Both the search query and the status filter only narrow the target accounts;
  // the status is matched against the effective status (the badge the user sees).
  const targetPages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const statuses = new Set(activeOption.statuses);
    return allTargetPages.filter((account) => {
      if (isFiltered && !statuses.has(getEffectiveStatus(account))) return false;
      if (query && !matchesSearch(account, query)) return false;
      return true;
    });
  }, [allTargetPages, searchQuery, isFiltered, activeOption]);

  // "Generate all" covers never-generated pages plus generated-but-stale ones.
  // The stale ones are counted here rather than bucketed into `queuedPages`,
  // so they stay in the target list and remain previewable and searchable.
  const totalCount = useMemo(
    () => queuedPages.length + allTargetPages.filter(needsRegeneration).length,
    [queuedPages, allTargetPages]
  );

  return {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    isFiltered,
    activeFilterLabel: activeOption.label,
    targetPages,
    queuedPages,
    hasAnyTarget: allTargetPages.length > 0,
    totalCount,
  };
};
